/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { EventBus } from "../../event-bus/event-bus"
import { EventBusEvent } from "../../event-bus/event-bus"
import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import type { ViewModel } from "../../viewmodel/viewmodel"

import type { ApplicationTypeService } from "./application-type-service"
import type { ApplicationTypeStatsService } from "./application-type-stats-service"
import type { ApplicationTypeStore } from "./application-type-store"
import type {
    ApplicationType,
    ApplicationTypeCategory,
    ApplicationTypePlatform,
    ApplicationTypeSDK,
    ThreatOverview
} from "./application-type-types"

export class ApplicationTypeViewModel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly store: ApplicationTypeStore,
        private readonly typeService: ApplicationTypeService,
        private readonly statsService: ApplicationTypeStatsService,
        private readonly eventBus: EventBus,
        private readonly logger: LoggerStore
    ) {
        this.eventBus.on(EventBusEvent.EnabledAppsChanged, async (enabledApps: number[]) => {
            try {
                await this.requestUpdateThreats(enabledApps)
            } catch (error) {
                const err = error as Error
                logger.logError({
                    message: err.message,
                    appError: err
                })
            }
        })
        this.eventBus.on(EventBusEvent.CurrentUserLoaded, (user) => {
            this.setUserAppTypes(user.applicationTypes as ApplicationType[])
        })
    }

    // Getters
    get allAppTypes(): ApplicationType[] {
        return this.store.allAppTypesArray
    }

    get userAppTypes(): ApplicationType[] {
        return this.store.userAppTypesArray
    }

    setUserAppTypes(appTypes: ApplicationType[]): void {
        this.store.setUserAppTypes(appTypes)
    }

    get hasAllAppTypes(): boolean {
        return this.allAppTypes.length > 0
    }

    get hasUserAppTypes(): boolean {
        return this.userAppTypes.length > 0
    }

    get aggregatedInsecure(): number {
        return this.store.aggregatedThreatOverview.insecure
    }

    get aggregatedOutdated(): number {
        return this.store.aggregatedThreatOverview.outdated
    }

    get aggregatedActive(): number {
        return this.store.aggregatedThreatOverview.active
    }

    recalculateAggregatedThreats(appTypeIds: number[]): void {
        this.store.recalculateAggregatedThreats(appTypeIds)
    }

    // Filter
    filterFavApplicationTypes(appTypes: number[]): ApplicationType[] {
        return this.userAppTypes.filter(({ id }) => id && appTypes.includes(id))
    }

    threatOverviewFor(appTypeId: number | null): ThreatOverview | null {
        if (!appTypeId) return null
        return this.store.threats.get(appTypeId) ?? null
    }

    // User app types come from /me, all app types only from the admin screens,
    // so the former is the one populated on the user-facing routes.
    appTypeFor(appTypeId: number | null): ApplicationType | null {
        if (!appTypeId) return null
        return this.store.userAppTypes.get(appTypeId) ?? this.store.allAppTypes.get(appTypeId) ?? null
    }

    iconVersionFor(appTypeId: number): number {
        return this.store.iconVersions.get(appTypeId) ?? 0
    }

    // Service calls
    async getAllAppTypes(): Promise<void> {
        await this.typeService.getApplicationTypes()
    }

    async getAppType(appTypeId: number): Promise<void> {
        await this.typeService.getApplicationType(appTypeId)
    }

    async updateAppType(appTypeId: number, description: string): Promise<Result<ApplicationType>> {
        return await this.typeService.updateApplicationType(appTypeId, description)
    }

    async getAppTypes(orgId: number): Promise<void> {
        await this.typeService.getApplicationTypes(orgId)
    }

    async getSDK(appTypeId: number): Promise<Result<ApplicationTypeSDK>> {
        return await this.typeService.getSDK(appTypeId)
    }

    async updateAppTypeIcon(appTypeId: number, iconFile: File | null): Promise<Result<void>> {
        return await this.typeService.updateApplicationTypeIcon(appTypeId, iconFile)
    }

    async createAppType(
        orgId: number,
        desc: string,
        packageName: string,
        platform: ApplicationTypePlatform,
        category: ApplicationTypeCategory
    ): Promise<Result<ApplicationType>> {
        return await this.typeService.createApplicationType(orgId, desc, packageName, platform, category)
    }

    async getThreatsOverview(appTypes: number[] | undefined): Promise<void> {
        try {
            await this.requestUpdateThreats(appTypes)
        } catch (error) {
            const err = error as Error
            this.logger.logError({
                message: err.message,
                appError: err
            })
        }
    }

    // Request
    private async requestUpdateThreats(appTypes: number[] | undefined): Promise<void> {
        // if there are no appTypes, we can return (e.g. for new user with 0 assigned apps)
        if (!appTypes || appTypes.length === 0) {
            return Promise.reject(
                new Error(
                    "To see dashboard data, you have to have some application assigned and selected as enabled in the settings."
                )
            )
        }

        // update application threats for selected appTypes
        const threatKeys = Array.from(this.store.threats.keys())
        // make difference between threat keys that are loaded and appTypes that are requested,
        // we want to load all appTypes that are not in threatKeys
        const diff = appTypes.filter((key) => !threatKeys.includes(key))
        await this.statsService.getThreatsOverview(diff)
    }
}
